import { assertPublicSiteCapabilities, executePublicRouteLoader, normalizePublicRouteDataPath, resolvePublicLoaderCanonicalPath, } from "../../core/index.js";
import { createPublicNoIndexPolicy, extendPublicHeadDescriptor, } from "../../seo/index.js";
import { renderPublicHtmlDocument, } from "../prerender/index.js";
export { createExpressPublicSitemapMiddleware, } from "./sitemap.js";
export function resolvePublicHttpRequest(site, rawPath, options = {}) {
    return site.resolvePath(rawPath, options);
}
export function createMemoryPublicHtmlCache(maximumEntries = 100) {
    if (!Number.isSafeInteger(maximumEntries) || maximumEntries < 1)
        throw new Error("maximumEntries must be a positive safe integer.");
    const entries = new Map();
    return {
        get(key) {
            const value = entries.get(key) ?? null;
            if (value) {
                entries.delete(key);
                entries.set(key, value);
            }
            return value;
        },
        set(key, value) {
            entries.delete(key);
            entries.set(key, value);
            while (entries.size > maximumEntries) {
                const oldestKey = entries.keys().next().value;
                if (!oldestKey)
                    break;
                entries.delete(oldestKey);
            }
        },
        delete(key) {
            entries.delete(key);
        },
    };
}
const NoStorePolicy = { kind: "no-store" };
function getDeliveryCachePolicy(route) {
    return route.delivery.kind === "spa-shell"
        ? NoStorePolicy
        : route.delivery.cache;
}
export function formatPublicCacheControl(policy, stale = false) {
    if (policy.kind === "no-store")
        return "no-store";
    return stale
        ? `public, max-age=0, stale-if-error=${policy.staleIfErrorSeconds}`
        : `public, max-age=${policy.maxAgeSeconds}, stale-if-error=${policy.staleIfErrorSeconds}`;
}
export function createPublicHttpRenderer(options) {
    assertPublicSiteCapabilities(options.site, {
        loaders: options.dataProviders,
        headResolvers: options.headResolvers,
    });
    const cache = options.cache ?? createMemoryPublicHtmlCache();
    const pending = new Map();
    const loaders = options.dataProviders ?? {};
    const resolveStatusHead = (pathname, status) => {
        const resolver = status === "not-found"
            ? options.resolveNotFoundHead
            : options.resolveUnavailableHead;
        if (resolver)
            return resolver(pathname);
        return {
            ...options.resolveHead(pathname),
            indexing: createPublicNoIndexPolicy(status),
            alternates: [],
            structuredData: [],
        };
    };
    const renderStatusDocument = async (pathname, status, retryAfter) => {
        const locale = options.resolveLocale(pathname);
        const state = options.createState(pathname, locale, {}, {
            status,
            ...(retryAfter === undefined ? {} : { retryAfter }),
        });
        let appHtml = "";
        let prerendered = false;
        try {
            appHtml = await options.renderRoute(pathname, state);
            prerendered = true;
        }
        catch (error) {
            options.onError?.(error);
        }
        return renderPublicHtmlDocument({
            shell: await options.readShellHtml(),
            appHtml,
            head: resolveStatusHead(pathname, status),
            state,
            dom: {
                ...options.dom,
                prerendered,
            },
        });
    };
    const renderNotFound = async (pathname, policy = {
        kind: "public",
        maxAgeSeconds: 60,
        staleIfErrorSeconds: 0,
    }) => ({
        html: await renderStatusDocument(pathname, "not-found"),
        statusCode: 404,
        headers: {
            "Cache-Control": formatPublicCacheControl(policy),
        },
    });
    const resolveHead = (resolution, data, canonicalPath = resolution.routePath) => {
        const resolverKey = resolution.route.headResolverKey;
        if (!resolverKey)
            return options.resolveHead(canonicalPath);
        const resolver = options.headResolvers?.[resolverKey];
        if (!resolver)
            throw new Error(`Missing public head resolver '${resolverKey}'.`);
        return extendPublicHeadDescriptor(options.resolveHead(canonicalPath), resolver({
            pathname: resolution.routePath,
            canonicalPath,
            route: resolution.route,
            params: resolution.params,
            data,
        }));
    };
    const renderOk = async (resolution, requestUrl, data, policy, canonicalPath = resolution.routePath) => {
        const generatedAt = Date.now();
        const expiresAt = policy.kind === "public"
            ? generatedAt + policy.maxAgeSeconds * 1_000
            : undefined;
        const state = options.createState(requestUrl, resolution.locale, data, {
            ...(canonicalPath === resolution.routePath ? {} : { canonicalPath }),
            generatedAt,
            ...(expiresAt === undefined ? {} : { expiresAt }),
        });
        const appHtml = await options.renderRoute(requestUrl, state);
        return {
            html: renderPublicHtmlDocument({
                shell: await options.readShellHtml(),
                appHtml,
                head: resolveHead(resolution, data, canonicalPath),
                state,
                dom: options.dom,
            }),
            statusCode: 200,
            headers: {
                "Cache-Control": formatPublicCacheControl(policy),
            },
        };
    };
    const renderLoaderResult = async (resolution, requestUrl, result, policy) => {
        switch (result.kind) {
            case "ok": {
                const canonicalPath = resolvePublicLoaderCanonicalPath(options.site, resolution, result.canonicalPath);
                return renderOk(resolution, requestUrl, result.data, policy, canonicalPath);
            }
            case "not-found":
                return renderNotFound(requestUrl, policy);
            case "redirect":
                return {
                    html: "",
                    statusCode: result.statusCode,
                    redirectTo: result.location,
                    headers: { "Cache-Control": "no-store" },
                };
            case "unavailable":
                return {
                    html: await renderStatusDocument(requestUrl, "unavailable", result.retryAfter),
                    statusCode: 503,
                    headers: {
                        "Cache-Control": "no-store",
                        ...(result.retryAfter === undefined ? {} : { "Retry-After": String(result.retryAfter) }),
                    },
                };
        }
    };
    const renderWithLoader = (resolution, rawUrl) => {
        const normalizedPath = normalizePublicRouteDataPath(rawUrl);
        const policy = getDeliveryCachePolicy(resolution.route);
        const existingPending = pending.get(normalizedPath);
        if (existingPending)
            return existingPending;
        const operation = (async () => {
            const now = Date.now();
            const cached = policy.kind === "public"
                ? await cache.get(normalizedPath)
                : null;
            try {
                if (policy.kind === "public" && cached && cached.expiresAt > now)
                    return {
                        html: cached.html,
                        statusCode: cached.statusCode,
                        headers: { "Cache-Control": formatPublicCacheControl(policy) },
                    };
                const result = await executePublicRouteLoader(resolution, loaders, { url: normalizedPath });
                const response = await renderLoaderResult(resolution, normalizedPath, result, policy);
                if (policy.kind === "public" && result.kind === "ok") {
                    const completedAt = Date.now();
                    await cache.set(normalizedPath, {
                        html: response.html,
                        statusCode: response.statusCode,
                        expiresAt: completedAt + policy.maxAgeSeconds * 1_000,
                        staleUntil: completedAt + (policy.maxAgeSeconds + policy.staleIfErrorSeconds) * 1_000,
                    });
                }
                return response;
            }
            catch (error) {
                options.onError?.(error);
                if (policy.kind === "public" && cached && cached.staleUntil > now)
                    return {
                        html: cached.html,
                        statusCode: cached.statusCode,
                        headers: { "Cache-Control": formatPublicCacheControl(policy, true) },
                    };
                return {
                    html: await renderStatusDocument(normalizedPath, "unavailable"),
                    statusCode: 503,
                    headers: { "Cache-Control": "no-store" },
                };
            }
            finally {
                pending.delete(normalizedPath);
            }
        })();
        pending.set(normalizedPath, operation);
        return operation;
    };
    const renderHtml = async (rawUrl, resolution) => {
        switch (resolution.type) {
            case "redirect":
                return {
                    html: "",
                    statusCode: resolution.statusCode,
                    redirectTo: resolution.location,
                    headers: resolution.vary ? { Vary: resolution.vary } : {},
                };
            case "not-found":
                return renderNotFound(resolution.routePath);
            case "app-shell": {
                const locale = options.resolveLocale(resolution.routePath);
                const state = options.createState(resolution.routePath, locale);
                return {
                    html: renderPublicHtmlDocument({
                        shell: await options.readShellHtml(),
                        appHtml: "",
                        head: options.resolveHead(resolution.routePath),
                        state,
                        dom: {
                            ...options.dom,
                            prerendered: false,
                        },
                    }),
                    statusCode: 200,
                    headers: { "Cache-Control": "no-store" },
                };
            }
            case "render": {
                if (resolution.route.delivery.kind === "spa-shell") {
                    const state = options.createState(resolution.routePath, resolution.locale);
                    return {
                        html: renderPublicHtmlDocument({
                            shell: await options.readShellHtml(),
                            appHtml: "",
                            head: resolveHead(resolution, {}),
                            state,
                            dom: {
                                ...options.dom,
                                prerendered: false,
                            },
                        }),
                        statusCode: 200,
                        headers: { "Cache-Control": "no-store" },
                    };
                }
                if (resolution.route.delivery.kind === "static" || resolution.route.delivery.kind === "hybrid-app-entry") {
                    const prerendered = await options.readPrerenderedHtml(resolution.routePath);
                    if (prerendered)
                        return {
                            html: prerendered,
                            statusCode: 200,
                            headers: {
                                "Cache-Control": formatPublicCacheControl(resolution.route.delivery.cache),
                            },
                        };
                }
                return renderWithLoader(resolution, rawUrl);
            }
        }
    };
    return {
        renderHtml,
        renderRequest(rawUrl, resolutionOptions = {}) {
            return renderHtml(rawUrl, resolvePublicHttpRequest(options.site, rawUrl, resolutionOptions));
        },
    };
}
//# sourceMappingURL=renderer.js.map